import { Vec3 } from './Vec3';

/**
 * 64-bit quaternion used for simulation-side orientation.
 *
 * Convention: `(x, y, z, w)` with `w` scalar, right-handed, rotating vectors
 * from body space into the parent reference frame:
 *
 *     v_parent = q * v_body * q^-1
 */
export class Quat {
  x: number;
  y: number;
  z: number;
  w: number;

  constructor(x = 0, y = 0, z = 0, w = 1) {
    this.x = x;
    this.y = y;
    this.z = z;
    this.w = w;
  }

  static identity(): Quat {
    return new Quat(0, 0, 0, 1);
  }

  clone(): Quat {
    return new Quat(this.x, this.y, this.z, this.w);
  }

  copy(q: Quat): this {
    this.x = q.x;
    this.y = q.y;
    this.z = q.z;
    this.w = q.w;
    return this;
  }

  set(x: number, y: number, z: number, w: number): this {
    this.x = x;
    this.y = y;
    this.z = z;
    this.w = w;
    return this;
  }

  static fromAxisAngle(axis: Vec3, angle: number): Quat {
    const n = axis.normalized();
    const h = angle * 0.5;
    const s = Math.sin(h);
    return new Quat(n.x * s, n.y * s, n.z * s, Math.cos(h));
  }

  /**
   * Builds the rotation whose body axes map to the supplied orthonormal parent
   * axes. `right`, `up`, `forward` must be mutually orthogonal unit vectors;
   * body +X maps to `right`, +Y to `up`, +Z to `forward`.
   *
   * The triple must be RIGHT-handed, i.e. `right x up === forward`. A
   * left-handed triple describes a reflection, which is not a rotation, and the
   * quaternion extracted from it is meaningless. Use `lookRotation` unless you
   * have already established the handedness.
   */
  static fromBasis(right: Vec3, up: Vec3, forward: Vec3): Quat {
    const m00 = right.x;
    const m01 = up.x;
    const m02 = forward.x;
    const m10 = right.y;
    const m11 = up.y;
    const m12 = forward.y;
    const m20 = right.z;
    const m21 = up.z;
    const m22 = forward.z;
    const trace = m00 + m11 + m22;
    const q = new Quat();
    if (trace > 0) {
      const s = 0.5 / Math.sqrt(trace + 1.0);
      q.w = 0.25 / s;
      q.x = (m21 - m12) * s;
      q.y = (m02 - m20) * s;
      q.z = (m10 - m01) * s;
    } else if (m00 > m11 && m00 > m22) {
      const s = 2.0 * Math.sqrt(1.0 + m00 - m11 - m22);
      q.w = (m21 - m12) / s;
      q.x = 0.25 * s;
      q.y = (m01 + m10) / s;
      q.z = (m02 + m20) / s;
    } else if (m11 > m22) {
      const s = 2.0 * Math.sqrt(1.0 + m11 - m00 - m22);
      q.w = (m02 - m20) / s;
      q.x = (m01 + m10) / s;
      q.y = 0.25 * s;
      q.z = (m12 + m21) / s;
    } else {
      const s = 2.0 * Math.sqrt(1.0 + m22 - m00 - m11);
      q.w = (m10 - m01) / s;
      q.x = (m02 + m20) / s;
      q.y = (m12 + m21) / s;
      q.z = 0.25 * s;
    }
    return q.normalize();
  }

  /**
   * Rotation whose body +Z axis points along `forward` and whose body +Y axis
   * lies as close to `up` as possible.
   *
   * Prefer this over `fromBasis` whenever you have a direction and a reference
   * up. `fromBasis` requires a *right-handed* triple, and geographic bases are
   * an easy trap: the natural (east, up, north) triple is left-handed, because
   * east x up = south, not north. Feeding that to `fromBasis` yields a
   * reflection rather than a rotation, and the resulting quaternion silently
   * turns the object upside down. This helper builds the triple itself and
   * cannot get it wrong.
   */
  static lookRotation(forward: Vec3, up: Vec3): Quat {
    const f = forward.normalized();
    let reference = up.normalized();
    if (Math.abs(reference.dot(f)) > 0.9999) reference = Vec3.anyPerpendicular(f);
    // right x up = forward requires right = up x forward.
    const right = Vec3.crossVectors(reference, f).normalize();
    const trueUp = Vec3.crossVectors(f, right).normalize();
    return Quat.fromBasis(right, trueUp, f);
  }

  /** Shortest-arc rotation taking unit vector `from` to unit vector `to`. */
  static fromUnitVectors(from: Vec3, to: Vec3): Quat {
    let r = from.dot(to) + 1;
    const q = new Quat();
    if (r < 1e-12) {
      // Anti-parallel: rotate 180 degrees about any perpendicular axis.
      const axis = Vec3.anyPerpendicular(from);
      q.set(axis.x, axis.y, axis.z, 0);
      return q.normalize();
    }
    const c = from.clone().cross(to);
    q.set(c.x, c.y, c.z, r);
    return q.normalize();
  }

  multiply(q: Quat): this {
    return this.multiplyQuaternions(this, q);
  }

  premultiply(q: Quat): this {
    return this.multiplyQuaternions(q, this);
  }

  multiplyQuaternions(a: Quat, b: Quat): this {
    const ax = a.x;
    const ay = a.y;
    const az = a.z;
    const aw = a.w;
    const bx = b.x;
    const by = b.y;
    const bz = b.z;
    const bw = b.w;
    this.x = ax * bw + aw * bx + ay * bz - az * by;
    this.y = ay * bw + aw * by + az * bx - ax * bz;
    this.z = az * bw + aw * bz + ax * by - ay * bx;
    this.w = aw * bw - ax * bx - ay * by - az * bz;
    return this;
  }

  conjugate(): this {
    this.x = -this.x;
    this.y = -this.y;
    this.z = -this.z;
    return this;
  }

  inverse(): Quat {
    return this.clone().conjugate().normalize();
  }

  length(): number {
    return Math.sqrt(this.x * this.x + this.y * this.y + this.z * this.z + this.w * this.w);
  }

  normalize(): this {
    const len = this.length();
    if (len === 0) {
      this.set(0, 0, 0, 1);
    } else {
      const inv = 1 / len;
      this.x *= inv;
      this.y *= inv;
      this.z *= inv;
      this.w *= inv;
    }
    return this;
  }

  /** Rotates `v` from body space into the parent frame. */
  rotate(v: Vec3, out = new Vec3()): Vec3 {
    // t = 2 * (q_vec x v);  v' = v + q_w * t + q_vec x t
    const tx = 2 * (this.y * v.z - this.z * v.y);
    const ty = 2 * (this.z * v.x - this.x * v.z);
    const tz = 2 * (this.x * v.y - this.y * v.x);
    out.x = v.x + this.w * tx + (this.y * tz - this.z * ty);
    out.y = v.y + this.w * ty + (this.z * tx - this.x * tz);
    out.z = v.z + this.w * tz + (this.x * ty - this.y * tx);
    return out;
  }

  /** Rotates `v` from the parent frame into body space. */
  rotateInverse(v: Vec3, out = new Vec3()): Vec3 {
    const tx = -2 * (this.y * v.z - this.z * v.y);
    const ty = -2 * (this.z * v.x - this.x * v.z);
    const tz = -2 * (this.x * v.y - this.y * v.x);
    out.x = v.x + this.w * tx - (this.y * tz - this.z * ty);
    out.y = v.y + this.w * ty - (this.z * tx - this.x * tz);
    out.z = v.z + this.w * tz - (this.x * ty - this.y * tx);
    return out;
  }

  /** Body-space axis vectors expressed in the parent frame. */
  axisX(out = new Vec3()): Vec3 {
    return this.rotate(UNIT_X, out);
  }
  axisY(out = new Vec3()): Vec3 {
    return this.rotate(UNIT_Y, out);
  }
  axisZ(out = new Vec3()): Vec3 {
    return this.rotate(UNIT_Z, out);
  }

  /**
   * Integrates orientation by angular velocity `omega` (rad/s, parent frame)
   * over `dt`, using the exact exponential map rather than a linear
   * approximation so that fast spins stay stable.
   */
  integrate(omega: Vec3, dt: number): this {
    const theta = omega.length() * dt;
    if (theta < 1e-12) return this;
    const axis = omega.normalized();
    const dq = Quat.fromAxisAngle(axis, theta);
    return this.premultiply(dq).normalize();
  }

  static slerp(a: Quat, b: Quat, t: number): Quat {
    let cos = a.x * b.x + a.y * b.y + a.z * b.z + a.w * b.w;
    const target = b.clone();
    if (cos < 0) {
      cos = -cos;
      target.x = -target.x;
      target.y = -target.y;
      target.z = -target.z;
      target.w = -target.w;
    }
    if (cos > 0.9995) {
      const q = new Quat(
        a.x + (target.x - a.x) * t,
        a.y + (target.y - a.y) * t,
        a.z + (target.z - a.z) * t,
        a.w + (target.w - a.w) * t,
      );
      return q.normalize();
    }
    const theta = Math.acos(cos);
    const sinTheta = Math.sin(theta);
    const wa = Math.sin((1 - t) * theta) / sinTheta;
    const wb = Math.sin(t * theta) / sinTheta;
    return new Quat(
      a.x * wa + target.x * wb,
      a.y * wa + target.y * wb,
      a.z * wa + target.z * wb,
      a.w * wa + target.w * wb,
    ).normalize();
  }

  /**
   * Rotation vector (axis * angle) that takes `this` to `target`, expressed in
   * the parent frame. This is the natural error signal for an attitude
   * controller.
   */
  errorTo(target: Quat): Vec3 {
    const delta = target.clone().multiply(this.inverse()).normalize();
    // Choose the shorter of the two equivalent rotations.
    if (delta.w < 0) {
      delta.x = -delta.x;
      delta.y = -delta.y;
      delta.z = -delta.z;
      delta.w = -delta.w;
    }
    const sinHalf = Math.hypot(delta.x, delta.y, delta.z);
    if (sinHalf < 1e-12) return new Vec3(0, 0, 0);
    const angle = 2 * Math.atan2(sinHalf, delta.w);
    const s = angle / sinHalf;
    return new Vec3(delta.x * s, delta.y * s, delta.z * s);
  }

  toArray(): [number, number, number, number] {
    return [this.x, this.y, this.z, this.w];
  }
}

const UNIT_X = new Vec3(1, 0, 0);
const UNIT_Y = new Vec3(0, 1, 0);
const UNIT_Z = new Vec3(0, 0, 1);
